import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type {
  CompanyCreateMemberDTO,
  CompanyMemberDTO,
  PaginatedCompanyMembersDTO,
} from '@shared/schemas/organization';
import {
  createCompanyMember,
  resendCompanyMemberInvitation,
  type CompanyMembersScope,
} from './company-api';
import { getCompanyErrorMessage } from './company-errors';
import { companyMembersQueryKeys, defaultCompanyMembersQuery } from './company-members-query';

export function useCreateCompanyMember(listPath: string, scope: CompanyMembersScope) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const submitLockRef = useRef(false);
  const mutation = useMutation({
    mutationFn: (input: CompanyCreateMemberDTO) => createCompanyMember(input, scope),
  });

  function resendInvitation(userId: string) {
    void resendCompanyMemberInvitation(userId, scope)
      .then(() => toast.success('E-mail d’activation renvoyé.'))
      .catch((error: unknown) => toast.error(getCompanyErrorMessage(error)));
  }

  async function submit(values: CompanyCreateMemberDTO) {
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    try {
      const result = await mutation.mutateAsync(values);
      const now = new Date().toISOString();
      const member: CompanyMemberDTO = {
        accountType: 'subscriber',
        banned: false,
        createdAt: now,
        email: values.email,
        emailVerified: false,
        id: result.data.userId,
        name: values.name,
        role: values.role,
        // Nouveau membre : aucune consommation avant sa première utilisation.
        tokensConsumed: 0,
        updatedAt: now,
      };
      queryClient.setQueryData<PaginatedCompanyMembersDTO>(
        companyMembersQueryKeys.list(scope, defaultCompanyMembersQuery),
        (previous) => previous && ({
          ...previous,
          items: [member, ...previous.items].slice(0, previous.meta.pageSize),
          meta: { ...previous.meta, total: previous.meta.total + 1 },
        }),
      );
      void queryClient.invalidateQueries({ queryKey: companyMembersQueryKeys.lists(scope) });

      if (result.data.invitation.status === 'sent') {
        toast.success(`Membre ${values.email} créé et invitation envoyée.`);
      } else {
        toast.warning('Le membre a été créé, mais l’invitation n’a pas pu être envoyée.', {
          action: {
            label: 'Renvoyer',
            onClick: () => resendInvitation(result.data.userId),
          },
        });
      }
      navigate(listPath, { replace: true });
    } finally {
      submitLockRef.current = false;
    }
  }

  return { isPending: mutation.isPending, submit };
}
